import {
  Component,
  inject,
  input,
  output,
  signal,
  computed,
  LOCALE_ID,
  ChangeDetectionStrategy,
  
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { UndeliveredReason } from '../../../../pages/admin-dashboard/undelivered-reasons/undelivered-reasons.model';
import { ConfirmationDialog } from '../../../../shared/ui/confirmation-dialog/confirmation-dialog';
import { UndeliveredReasonsService } from '../../../../services/undelivered-reasons/undelivered-reasons.service';
import { HasScopeDirective } from '../../../../services/scope/has-scope.directive';
import { SCOPES } from '../../../../services/scope/scope-constants'; 

export type DeleteErrorType = 'SYSTEM_DEFINED' | 'IN_USE' | 'UNKNOWN' | null;

@Component({
  selector: 'app-undelivered-reasons-delete-component',
  standalone: true,
  imports: [CommonModule, ConfirmationDialog, HasScopeDirective],
  templateUrl: './undelivered-reasons-delete-component.html',
  styleUrl: './undelivered-reasons-delete-component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UndeliveredReasonsDeleteComponent {
  private undeliveredReasonsService = inject(UndeliveredReasonsService);
  private locale = inject(LOCALE_ID);

  protected readonly SCOPES = SCOPES;

  reason = input.required<UndeliveredReason>();
  onClose = output<void>();
  onSuccess = output<string>();

  // State Signals
  isSubmitting = signal<boolean>(false);
  deleteError = signal<string | null>(null);

  // Holds errors that come back from the delete attempt (IN_USE / UNKNOWN)
  private requestErrorType = signal<DeleteErrorType>(null);

  // isEditable === false -> System-defined, can't be deleted
  private isSystemDefined = computed(() => !this.reason().isEditable);

  // Final error type shown in the template
  errorType = computed<DeleteErrorType>(() =>
    this.isSystemDefined() ? 'SYSTEM_DEFINED' : this.requestErrorType()
  );

  isArabic = computed(() => typeof this.locale === 'string' && this.locale.startsWith('ar'));

  reasonName = computed(() => {
    const item = this.reason();
    return this.isArabic() ? item.titleAr || item.titleEn : item.titleEn;
  });

  // Localized strings using $localize
  readonly dialogTitle = $localize`:@@deleteReasonTitle:Delete Reason?`;
  readonly dialogDescription = $localize`:@@adminProductsDeleteConfirm:Are you sure you want to delete`;
  readonly dialogConfirmText = $localize`:@@adminProductsDeleteConfirmButton:Delete`;
  readonly dialogCancelText = $localize`:@@adminProductsDeleteCancel:Cancel`;

  successMessage = computed(() => {
    const name = this.reasonName();
    return $localize`:@@deleteSuccessMessage:Undelivered reason '${name}:itemName:' deleted successfully`;
  });

  confirmDelete(): void {
    if (this.isSubmitting() || this.isSystemDefined()) return;

    const item = this.reason();
    this.isSubmitting.set(true);
    this.deleteError.set(null);

    this.undeliveredReasonsService.deleteReason(item.id).subscribe({
      next: () => {
        this.isSubmitting.set(false);
        this.onSuccess.emit(this.successMessage());
      },
      error: (err) => {
        this.isSubmitting.set(false);

        // التعامل مع خطأ انقطاع السيرفر ERR_CONNECTION_REFUSED
        if (err?.status === 0) {
          const connErrorMsg = this.isArabic()
            ? 'تعذر الاتصال بالخادم، يرجى التأكد من تشغيل الـ Server أو الاتصال بالشبكة.'
            : 'Server Connection Refused. Please check network connection.';
          this.deleteError.set(connErrorMsg);
          return;
        }

        const defaultMessage = $localize`:@@deleteErrorMessage:An error occurred while deleting, please try again.`;
        const extractedMessage =
          err?.error?.messageAr && this.isArabic()
            ? err.error.messageAr
            : err?.error?.messageEn || err?.error?.message || defaultMessage;

        if (err?.status === 409 || err?.status === 400) {
          this.requestErrorType.set('IN_USE');
        } else {
          this.requestErrorType.set('UNKNOWN');
        }

        this.deleteError.set(extractedMessage);
      },
    });
  }

  cancel(): void {
    if (this.isSubmitting()) return;
    this.onClose.emit();
  }
}